import * as THREE from "three";
import { evaluateExpression, toNumber } from "./evaluateBindings";

export function degToRad(value) {
  return toNumber(value, 0) * (Math.PI / 180);
}

function resolveAxis(value, context = {}, fallback = 0) {
  const resolved = evaluateExpression(value ?? fallback, context, fallback);
  return toNumber(resolved, fallback);
}

function toVector3(value = {}, context = {}) {
  return new THREE.Vector3(
    resolveAxis(value.x, context, 0),
    resolveAxis(value.y, context, 0),
    resolveAxis(value.z, context, 0)
  );
}

function toEulerRadiansFromDegrees(value = {}, context = {}) {
  return new THREE.Euler(
    degToRad(resolveAxis(value.x, context, 0)),
    degToRad(resolveAxis(value.y, context, 0)),
    degToRad(resolveAxis(value.z, context, 0)),
    "XYZ"
  );
}

function matrixFromTransform(transform = {}, context = {}) {
  const position = toVector3(transform.position || {}, context);
  const rotation = toEulerRadiansFromDegrees(transform.rotation || {}, context);
  const quaternion = new THREE.Quaternion().setFromEuler(rotation);
  const scale = new THREE.Vector3(1, 1, 1);

  return new THREE.Matrix4().compose(position, quaternion, scale);
}

function matrixFromWorldTransform(world = {}) {
  const position = new THREE.Vector3(
    toNumber(world?.position?.x, 0),
    toNumber(world?.position?.y, 0),
    toNumber(world?.position?.z, 0)
  );

  const rotation = new THREE.Euler(
    toNumber(world?.rotation?.x, 0),
    toNumber(world?.rotation?.y, 0),
    toNumber(world?.rotation?.z, 0),
    "XYZ"
  );

  const quaternion = new THREE.Quaternion().setFromEuler(rotation);
  const scale = new THREE.Vector3(1, 1, 1);

  return new THREE.Matrix4().compose(position, quaternion, scale);
}

function transformFromMatrix(matrix) {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();

  matrix.decompose(position, quaternion, scale);

  const rotation = new THREE.Euler().setFromQuaternion(quaternion, "XYZ");

  return {
    position: {
      x: Number(position.x.toFixed(6)),
      y: Number(position.y.toFixed(6)),
      z: Number(position.z.toFixed(6)),
    },
    rotation: {
      x: Number(rotation.x.toFixed(6)),
      y: Number(rotation.y.toFixed(6)),
      z: Number(rotation.z.toFixed(6)),
    },
  };
}

export function createWorldTransform(parent = null, local = null, context = {}) {
  const parentMatrix = parent
    ? matrixFromWorldTransform(parent)
    : new THREE.Matrix4();

  const localMatrix = matrixFromTransform(local || {}, context);

  const worldMatrix = parentMatrix.clone().multiply(localMatrix);

  return transformFromMatrix(worldMatrix);
}